import React, { useState, useEffect } from 'react'
import { useTournaments } from '../../contexts/TournamentContext'
import { useAuth } from '../../contexts/AuthContext'
import { apiUrl } from '../../config/api'
import { useWebSocket } from '../../contexts/WebSocketContext'
import { showSuccess, showError } from '../Toast/Toast'
import QuestAccountModal from '../Modal/QuestAccountModal'
import InviteCodeModal from '../Modal/InviteCodeModal'
import PremiumTournamentModal from '../Modal/PremiumTournamentModal'
import VerifyEmailModal from '../Modal/VerifyEmailModal'
import './Tournaments.css'

const Tournaments = ({ onShowTournamentDetails, initialTab = 'big', onNavigateToAuth }) => {
  const { user, profile, isGuest, isVerified } = useAuth()
  const {
    tournaments,
    loading,
    error,
    listTournaments,
    joinTournament,
    leaveTournament,
    getTournamentStandings,
    clearError
  } = useTournaments()

  const [activeTab, setActiveTab] = useState(initialTab)
  const [filterStatus, setFilterStatus] = useState('registering')
  const [currentTime, setCurrentTime] = useState(new Date())
  const [showQuestModal, setShowQuestModal] = useState(false)
  const [tournamentHistory, setTournamentHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Invite Code State
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState(null)

  // Premium Tournament Modal State
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [userPremiumStatus, setUserPremiumStatus] = useState(
    (user?.account_type === 'premium') || (profile?.account_type === 'premium')
  )

  // Verify Email Modal State
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false)

  const { subscribe } = useWebSocket()

  // WebSocket Updates for Tournament List
  useEffect(() => {
    if (!subscribe) return

    const handleUpdate = () => {
      console.log('Received tournament list update via WS, refreshing...')
      listTournaments(filterStatus)
    }

    const unsubs = [
      subscribe('tournament_created', handleUpdate),
      subscribe('tournament_update', handleUpdate),
      subscribe('tournament_started', handleUpdate),
      subscribe('tournament_deleted', handleUpdate)
    ]

    return () => unsubs.forEach(u => u())
  }, [subscribe, listTournaments, filterStatus])

  useEffect(() => {
    if (activeTab === 'history') {
      loadTournamentHistory()
    } else if (activeTab === 'my') {
      listTournaments('my')
    } else {
      listTournaments(filterStatus)
    }
  }, [listTournaments, filterStatus, activeTab])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, clearError])

  // Update countdown timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000) // Update every second

    return () => clearInterval(timer)
  }, [])

  // Check premium status when component mounts or user changes
  useEffect(() => {
    const hasPremiumAccount = (user?.account_type === 'premium') || (profile?.account_type === 'premium')
    setUserPremiumStatus(hasPremiumAccount)
  }, [user?.account_type, profile?.account_type])

  const handleJoinTournament = (tournamentId) => {
    // Check if user is a guest account
    if (isGuest) {
      setShowQuestModal(true)
      return
    }

    // Check if user email is verified
    if (!isVerified) {
      setShowVerifyEmailModal(true)
      return
    }

    // Check if tournament is invite-only
    const tournament = tournaments.find(t => t.id === tournamentId)
    const isInviteOnly = tournament && tournament.access_type === 'invite_only'

    // Check if user has premium - required for public tournaments only
    if (!userPremiumStatus && !isInviteOnly) {
      setShowPremiumModal(true)
      return
    }

    if (isInviteOnly) {
      // Open invite code modal
      setSelectedTournament(tournament)
      setShowInviteModal(true)
      return
    }

    // Regular join
    performJoin(tournamentId, null)
  }

  const performJoin = async (tournamentId, code) => {
    // Find tournament to check access type
    const tournament = tournaments.find(t => t.id === tournamentId)
    const isInviteOnly = tournament && tournament.access_type === 'invite_only'

    // Check if user has premium - required for public tournaments
    if (!userPremiumStatus && !isInviteOnly) {
      setShowInviteModal(false)
      setSelectedTournament(null)
      setShowPremiumModal(true)
      return
    }

    try {
      await joinTournament(tournamentId, code)
      showSuccess('Successfully joined tournament!')
      setShowInviteModal(false)
      setSelectedTournament(null)
    } catch (err) {
      console.error('Failed to join tournament:', err)
      showError(err.message || 'Failed to join tournament. Please try again.')
    }
  }

  const handleLeaveTournament = async (tournamentId) => {
    try {
      await leaveTournament(tournamentId)
      showSuccess('Successfully left tournament!')
    } catch (err) {
      console.error('Failed to leave tournament:', err)
      showError('Failed to leave tournament. Please try again.')
    }
  }


  const loadTournamentHistory = async () => {
    setLoadingHistory(true)
    try {
      // Fetch completed tournaments with their standings
      const completedTournaments = await listTournaments('completed')


      // For each tournament, get its standings
      const tournamentsWithStandings = await Promise.all(
        completedTournaments.tournaments?.map(async (tournament) => {
          try {
            const standings = await getTournamentStandings(tournament.id)
            return {
              ...tournament,
              standings: standings || []
            }
          } catch (err) {
            console.error('Failed to load standings for tournament:', tournament.id)
            return {
              ...tournament,
              standings: []
            }
          }
        }) || []
      )

      // Only show RiftChess Invitational tournaments in history
      // Show RiftChess Invitational, Weekly, and Monthly tournaments in history
      const filteredHistory = tournamentsWithStandings.filter(t => {
        const name = t.name ? t.name.toLowerCase() : ''
        return name.includes('riftchess invitational') || 
               name.includes('weekly') || 
               name.includes('monthly')
      })
      setTournamentHistory(filteredHistory)
    } catch (err) {
      console.error('Failed to load tournament history:', err)
      setTournamentHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const formatTimeControl = (timeControl) => {
    if (!timeControl) return 'Classic'
    const { initial_time, increment } = timeControl
    if (!initial_time && !increment) return 'Classic'
    const minutes = Math.floor(initial_time / 60)
    return `${minutes}+${increment}`
  }

  const formatCountdown = (startTime) => {
    if (!startTime) return '';

    const now = currentTime;
    const start = new Date(startTime);
    const diff = start - now;

    if (diff <= 0) return 'Starting soon';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  const getEntryDisplayText = (tournament) => {
    const isInviteOnly = tournament.name.toLowerCase().includes('invitational');
    const entryFee = tournament.entry_fee || 0;
    const isPremiumRequired = tournament.access_type === 'premium';

    if (isInviteOnly) return 'Invite Only';

    let entryText = entryFee > 0 ? `$${entryFee} entry` : 'Free entry';

    if (isPremiumRequired) {
      entryText += ', Premium required';
    }

    return entryText;
  }

  const formatPlayerCount = (tournament) => {
    const { players_count, max_players } = tournament
    return max_players > 0 ? `${players_count}/${max_players}` : players_count.toString()
  }

  const getTournamentStatus = (tournament) => {
    const now = new Date()
    const startTime = tournament.start_time ? new Date(tournament.start_time) : null
    const name = tournament.name.toLowerCase()



    if (tournament.status === 'registering') {
      if (tournament.max_players > 0 && tournament.players_count >= tournament.max_players) {
        return { text: 'FULL', class: 'full' }
      }
      if (startTime && now >= startTime) {
        return { text: 'STARTING', class: 'starting' }
      }
      return { text: 'OPEN', class: 'open' }
    }

    return { text: tournament.status.toUpperCase(), class: tournament.status }
  }

  const isUserRegistered = (tournament) => {
    if (!user?.id || !tournament.players) return false
    return tournament.players.some(player => player.id === user.id)
  }

  const handleUpgradeToPremium = () => {
    setShowPremiumModal(false)
    // Navigate to premium page in Dashboard - pass onBack function
    if (onNavigateToAuth) {
      onNavigateToAuth('premium')
    }
  }

  const handleCreateAccount = () => {
    setShowQuestModal(false)
    // Navigate to auth page
    if (onNavigateToAuth) {
      onNavigateToAuth('auth')
    }
  }


  const filterTournaments = (type) => {
    // Filter out test tournaments
    const filteredTournaments = tournaments.filter(t => !t.name.toLowerCase().includes('test'))

    if (type === 'big') {
      const bigTournaments = filteredTournaments.filter(t => !t.max_players || t.max_players > 32)
      // Sort tournaments by priority: Invitational first, Monthly second, Weekly third, Daily fourth, then others
      return bigTournaments.sort((a, b) => {
        const getOrderPriority = (tournament) => {
          const name = tournament.name.toLowerCase()
          if (name.includes('invitational')) return 1
          if (name.includes('monthly')) return 2
          if (name.includes('weekly')) return 3
          if (name.includes('daily')) return 4
          return 5
        }
        return getOrderPriority(a) - getOrderPriority(b)
      })
    }
    return filteredTournaments.filter(t => t.max_players && t.max_players <= 32)
  }

  const calculatePrizePool = (tournament) => {
    const entryFee = tournament.prize_pool?.entry_fee || 5;
    const maxPlayers = tournament.max_players || 8;
    const totalEntry = entryFee * maxPlayers;
    const platformFee = totalEntry * 0.03; // 3% platform fee
    const prizePool = totalEntry - platformFee;
    return Math.round(prizePool);
  };

  const getBigTournamentMinimum = (tournament) => {
    // Determine tournament type based on name or other properties
    const name = tournament.name.toLowerCase();
    if (name.includes('monthly')) {
      return tournament.prize_pool?.total_amount || 1000;
    } else if (name.includes('weekly')) {
      return tournament.prize_pool?.total_amount || 200;
    } else if (name.includes('daily')) {
      return tournament.prize_pool?.total_amount || 30;
    }
    return 100; // default for big tournaments
  };

  const getScheduledStartTime = (tournament) => {
    const name = tournament.name.toLowerCase();
    const now = new Date();

    if (name.includes('monthly')) {
      // February 7th, 2026 at 17:00 CET (stored as 16:00 UTC in database)
      const februaryTournament = new Date('2026-02-07T17:00:00+01:00'); // CET timezone
      return februaryTournament;
    } else if (name.includes('weekly')) {
      // Next Saturday at 18:00 CET
      const nextSaturday = new Date(now);
      nextSaturday.setDate(now.getDate() + (6 - now.getDay() + 7) % 7);
      nextSaturday.setHours(18, 0, 0, 0);
      if (nextSaturday <= now) {
        nextSaturday.setDate(nextSaturday.getDate() + 7);
      }
      return nextSaturday;
    }

    return tournament.start_time ? new Date(tournament.start_time) : null;
  };

  const getPrizePoolDisplay = (tournament) => {
    return tournament.prize_pool?.total_amount ?
      `$${tournament.prize_pool.total_amount}` : "TBD";
  };

  const calculatePrizeForPosition = (tournament, position) => {
    if (!tournament.is_money_prize || !tournament.prize_pool || !tournament.prize_pool.total_amount || !tournament.prize_pool.distribution) {
      return 0
    }

    const distribution = Array.isArray(tournament.prize_pool.distribution)
      ? tournament.prize_pool.distribution
      : JSON.parse(tournament.prize_pool.distribution || '[]')

    const playerCount = tournament.players_count || 0
    const effectiveDistribution = distribution.slice(0, playerCount)

    // If fewer players than prizes, redistribute remaining prize money equally
    if (playerCount < distribution.length && playerCount > 0) {
      const totalDistributedPercentage = effectiveDistribution.reduce((sum, percent) => sum + percent, 0)
      const remainingPercentage = 100 - totalDistributedPercentage
      const bonusPerPlayer = remainingPercentage / playerCount

      for (let i = 0; i < effectiveDistribution.length; i++) {
        effectiveDistribution[i] += bonusPerPlayer
      }
    }

    // Position is 1-indexed, array is 0-indexed
    const arrayIndex = position - 1
    if (arrayIndex < effectiveDistribution.length) {
      const percentage = effectiveDistribution[arrayIndex]
      return (tournament.prize_pool.total_amount * percentage / 100).toFixed(2)
    }

    return 0
  };

  const renderTournamentCard = (tournament, fromTab = 'big') => {
    const status = getTournamentStatus(tournament)
    const isCreator = tournament.creator?.id === user?.id
    const isRegistered = isUserRegistered(tournament)
    const prizePoolDisplay = getPrizePoolDisplay(tournament);
    const isBigTournament = (!tournament.max_players || tournament.max_players > 32);
    const startTime = tournament.start_time ? new Date(tournament.start_time) : null;
    const name = tournament.name.toLowerCase()
    const isComingSoon = fromTab === 'sitgo';

    if (isComingSoon) {
      return (
        <div key={tournament.id} className="sitgo-coming-soon-wrapper">
          <div className="tournament-card disabled-tournament">
            {/* Tournament card content - SIMPLIFIED FOR SIT&GO COMING SOON */}
            <div className="tournament-top-row">
              <span className="tournament-entry" style={{ color: '#666' }}>{getEntryDisplayText(tournament)}</span>
               <div className={`tournament-status ${status.class}`}>{status.text}</div>
            </div>
             <div className="tournament-header">
                <div className="tournament-info">
                   <h3 className="tournament-name">{tournament.name.replace(/\s*\(bracket\)|\s*\(round_robin\)|\s*Sit & Go/gi, '')}</h3>
                   <div className="tournament-meta">
                      <span className="tournament-time">{formatTimeControl(tournament.time_control)}</span>
                   </div>
                </div>
             </div>
             {/* Coming Soon Overlay */}
             <div className="sitgo-coming-soon-overlay">
               <span className="sitgo-coming-soon-text">COMING SOON</span>
             </div>
          </div>
        </div>
      )
    }

    return (
      <div key={tournament.id} className="tournament-card">
        <div className="tournament-top-row">
          <span
            className="tournament-entry"
            style={{
              color: tournament.name.toLowerCase().includes('invitational') ? '' : '#888'
            }}
          >
            {getEntryDisplayText(tournament)}
            {tournament.access_type === 'premium' && !tournament.name.toLowerCase().includes('invitational') && (
              <span style={{ color: '#ef4444', marginLeft: '2px', fontSize: '1.2rem', fontWeight: 'bold' }}>*</span>
            )}
          </span>
          <div className={`tournament-status ${status.class}`}>
            {status.text}
          </div>
        </div>

        {isBigTournament && (
          <>
            <div className="tournament-prize-row">
              <span className="prize-label">PRIZE POOL:</span>
              <span className="prize-value-inline">{prizePoolDisplay}</span>
            </div>
            {startTime && (
              <div className="tournament-countdown-row">
                <span className="countdown-label">STARTS IN:</span>
                <span className="countdown-value">{formatCountdown(startTime)}</span>
              </div>
            )}
          </>
        )}

        <div className="tournament-header">
          <div className="tournament-info">
            <h3 className="tournament-name">{tournament.name.replace(/\s*\(bracket\)|\s*\(round_robin\)|\s*Sit & Go/gi, '')}</h3>
            <p className="tournament-description">{isBigTournament ? 'Join the competition!' : 'Starts when full!'}</p>
            <div className="tournament-meta">
              {isBigTournament ? (
                <div className="tournament-meta-horizontal">
                  <span className="tournament-type">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                  <span className="tournament-time">{formatTimeControl(tournament.time_control)}</span>
                  {tournament.match_format && (
                    <span className="tournament-time">{tournament.match_format}</span>
                  )}
                </div>
              ) : (
                <>
                  <span className="tournament-type">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                  <span className="tournament-time">{formatTimeControl(tournament.time_control)}</span>
                  {tournament.match_format && (
                    <span className="tournament-time">{tournament.match_format}</span>
                  )}
                </>
              )}
            </div>
          </div>
          {!isBigTournament && (
            <div className="tournament-prize">
              <div className="prize-label">PRIZE POOL</div>
              <div className="prize-value">{prizePoolDisplay}</div>
            </div>
          )}
        </div>

        <div className="tournament-details">
          <div className={`detail-row ${isBigTournament ? 'detail-row-separated' : ''}`}>
            <span className="detail-label">{isBigTournament ? 'Registered Players:' : 'Players:'}</span>
            <span className="detail-value">{formatPlayerCount(tournament)}</span>
          </div>

          {startTime && (
            <div className="detail-row">
              <span className={`detail-label ${isBigTournament ? 'start-date-label' : ''}`}>Start Date:</span>
              <span className={`detail-value ${isBigTournament ? 'start-date-value' : ''}`}>
                {isBigTournament ? (
                  <>
                    {startTime.toLocaleDateString('en-GB', {timeZone: 'Europe/Berlin'})} {startTime.toLocaleTimeString('en-GB', {timeZone: 'Europe/Berlin', hour12: false})} CET
                  </>
                ) : (
                  startTime.toLocaleString('en-GB', {timeZone: 'Europe/Berlin', hour12: false}) + ' CET'
                )}
              </span>
            </div>
          )}
        </div>

        <div className="tournament-actions">
          {tournament.status === 'registering' && !isCreator && (
            <>
              {isRegistered ? (
                <button
                  className="leave-btn"
                  onClick={() => handleLeaveTournament(tournament.id)}
                  disabled={loading}
                >
                  Unregister
                </button>
              ) : (
                <button
                  className="join-btn"
                  onClick={() => handleJoinTournament(tournament.id)}
                  disabled={loading || (tournament.max_players > 0 && tournament.players_count >= tournament.max_players)}
                >
                  {tournament.max_players > 0 && tournament.players_count >= tournament.max_players
                    ? 'Full'
                    : 'Register'
                  }
                </button>
              )}
              <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                Details
              </button>
            </>
          )}

          {isCreator && (
            <>
              <span className="creator-badge">Your Tournament</span>
              <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                Details
              </button>
            </>
          )}

          {tournament.status === 'active' && (
            <>
              <button className="spectate-btn">
                Spectate
              </button>
              <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
                Details
              </button>
            </>
          )}
          {tournament.status === 'completed' && (
            <button className="details-btn" onClick={() => onShowTournamentDetails?.(tournament.id, fromTab)}>
               Details
            </button>
          )}
        </div>
      </div>
    )
  }

  // Toggle for Test Tab - set to false to hide in production
  const SHOW_TEST_TAB = false

  return (
    <div className="tournaments-page">
      <VerifyEmailModal 
        isOpen={showVerifyEmailModal}
        onClose={() => setShowVerifyEmailModal(false)}
      />

      <div className="tournaments-header">
        <h1>Tournaments</h1>
        <p>Join or create chess tournaments with players around the world</p>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
      </div>

      <div className="tournaments-tabs">
        <button
          className={`tab-btn ${activeTab === 'big' ? 'active' : ''}`}
          onClick={() => setActiveTab('big')}
        >
          Big Tournaments
        </button>
        {user && (
          <button
            className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            My Tournaments
          </button>
        )}
        <button
          className={`tab-btn ${activeTab === 'sitgo' ? 'active' : ''}`}
          onClick={() => setActiveTab('sitgo')}
        >
          Sit & Go
        </button>
        {SHOW_TEST_TAB && (
          <button
            className={`tab-btn ${activeTab === 'test' ? 'active' : ''}`}
            onClick={() => setActiveTab('test')}
          >
            Test
          </button>
        )}
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      <div className="tournaments-filters">
        {activeTab !== 'my' && (
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="registering">Open for Registration</option>
            <option value="active">In Progress</option>
            <option value="completed">Completed</option>
            <option value="">All Tournaments</option>
          </select>
        )}
      </div>

      <div className="tournaments-content">
        {activeTab === 'big' && (
          <div className="big-tournaments">
            <div className="section-header">
              <h2>Big Tournaments</h2>
            </div>

            <div className="tournaments-grid">
              {filterTournaments('big').map(tournament => renderTournamentCard(tournament, 'big'))}

              {filterTournaments('big').length === 0 && !loading && (
                <div className="empty-state">
                  <p>No big tournaments available right now</p>
                  <p>Check back soon for upcoming events!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sitgo' && (
          <div className="sitgo-tournaments">
            <div className="section-header">
              <h2>Sit & Go Tournaments</h2>
              <p>Quick tournaments that start when full - Join anytime!</p>
            </div>


            <div className="tournament-format-section">
              <div className="format-header">
                <h3>Single Elimination Bracket</h3>
                <p>Fast-paced knockout tournaments - Winner takes all!</p>
              </div>
              <div className="tournaments-grid">
                {filterTournaments('sitgo').filter(t => t.type === 'bracket').map(tournament => renderTournamentCard(tournament, 'sitgo'))}

                {filterTournaments('sitgo').filter(t => t.type === 'bracket').length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No bracket tournaments available</p>
                    <p>Create one to get started!</p>
                  </div>
                )}
              </div>
            </div>

            <div className="tournament-format-section">
              <div className="format-header">
                <h3>Round Robin</h3>
                <p>Everyone plays everyone - Most points wins!</p>
              </div>
              <div className="tournaments-grid">
                {filterTournaments('sitgo').filter(t => t.type === 'round_robin').map(tournament => renderTournamentCard(tournament, 'sitgo'))}

                {filterTournaments('sitgo').filter(t => t.type === 'round_robin').length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No round robin tournaments available</p>
                    <p>Create one to get started!</p>
                  </div>
                )}
              </div>
            </div>

            <div className="tournament-format-section">
              <div className="format-header">
                <h3>Swiss & Arena</h3>
                <p>Dynamic pairings and non-elimination formats</p>
              </div>
              <div className="tournaments-grid">
                {filterTournaments('sitgo').filter(t => t.type === 'swiss' || t.type === 'arena').map(tournament => renderTournamentCard(tournament, 'sitgo'))}

                {filterTournaments('sitgo').filter(t => t.type === 'swiss' || t.type === 'arena').length === 0 && !loading && (
                  <div className="empty-state">
                    <p>No swiss or arena tournaments available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'my' && (
          <div className="my-tournaments">
            <div className="section-header">
              <h2>My Tournaments</h2>
              <p>Tournaments you have joined or created</p>
            </div>
            
            <div className="tournaments-grid">
              {tournaments.map(tournament => renderTournamentCard(tournament, 'my'))}

              {tournaments.length === 0 && !loading && (
                <div className="empty-state">
                  <p>You haven't joined any tournaments yet.</p>
                  <button className="join-btn" onClick={() => setActiveTab('big')} style={{marginTop: '1rem'}}>
                    Browse Tournaments
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'test' && (
          <div className="test-tournaments">
            <div className="section-header">
              <h2>Test View</h2>
              <p>Showing ALL available tournaments (ignoring player limits)</p>
            </div>
            
            <div className="tournaments-grid">
              {/* Show all loaded tournaments directly without size filtering */}
              {tournaments.map(tournament => renderTournamentCard(tournament, 'test'))}

              {tournaments.length === 0 && !loading && (
                <div className="empty-state">
                  <p>No tournaments found matching current filter ({filterStatus})</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="tournament-history">
            <div className="section-header">
              <h2>Tournament History</h2>
              <p>Complete results and standings from all past tournaments</p>
            </div>

            {loadingHistory ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading tournament history...</p>
              </div>
            ) : (
              <div className="history-list">
                {tournamentHistory.length > 0 ? (
                  tournamentHistory.map(tournament => (
                    <div key={tournament.id} className="history-tournament-card">
                      <div className="history-header">
                        <div className="history-info">
                          <h3 className="history-name">{tournament.name}</h3>
                          <div className="history-meta">
                            <span className="history-type">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                            <span className="history-time-control">{formatTimeControl(tournament.time_control)}</span>
                            {tournament.match_format && (
                              <span className="history-time-control">{tournament.match_format}</span>
                            )}
                            <span className="history-date">
                              {tournament.start_time || tournament.started_at || tournament.created_at ?
                                new Date(tournament.start_time || tournament.started_at || tournament.created_at).toLocaleDateString('en-GB', {
                                  timeZone: 'Europe/Berlin',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : 'Date unknown'
                              }
                            </span>
                          </div>
                        </div>
                        <div className="history-actions">
                          <button
                            className="history-details-btn"
                            onClick={() => onShowTournamentDetails?.(tournament.id, 'history')}
                          >
                            View Details
                          </button>
                        </div>
                      </div>

                      {tournament.standings && tournament.standings.length > 0 && (
                        <div className="history-standings">
                          <h4>Final Standings</h4>

                          {/* Top 3 Podium Standings */}
                          <div className="podium-standings">
                            {tournament.standings.slice(0, 3).map((standing, index) => (
                              <div key={standing.id || index} className="podium-standing">
                                <span className={`standing-position ${
                                  index === 0 ? 'first' :
                                  index === 1 ? 'second' :
                                  index === 2 ? 'third' : ''
                                }`}>
                                  #{index + 1}
                                </span>
                                <span className="standing-name">{standing.user?.username || 'Unknown'}</span>
                                <span className="standing-prize">
                                  {(() => {
                                    const prizeAmount = calculatePrizeForPosition(tournament, index + 1)
                                    return prizeAmount > 0 ? `$${prizeAmount}` : `${standing.points || 0} pts`
                                  })()}
                                </span>
                                <div className="standing-record">
                                  <span className="wins">W:{standing.wins || 0}</span>
                                  <span className="draws">D:{standing.draws || 0}</span>
                                  <span className="losses">L:{standing.losses || 0}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Remaining Standings */}
                          {tournament.standings.length > 3 && (
                            <div className="remaining-standings">
                              {tournament.standings.slice(3, 6).map((standing, index) => (
                                <div key={standing.id || index} className="standing-row">
                                  <span className="standing-position">#{index + 4}</span>
                                  <span className="standing-name">{standing.user?.username || 'Unknown'}</span>
                                  <span className="standing-prize">
                                    {(() => {
                                      const prizeAmount = calculatePrizeForPosition(tournament, index + 4)
                                      return prizeAmount > 0 ? `$${prizeAmount}` : `${standing.points || 0} pts`
                                    })()}
                                  </span>
                                  <div className="standing-record">
                                    <span className="wins">W:{standing.wins || 0}</span>
                                    <span className="draws">D:{standing.draws || 0}</span>
                                    <span className="losses">L:{standing.losses || 0}</span>
                                  </div>
                                </div>
                              ))}
                              {tournament.standings.length > 6 && (
                                <div className="standings-more">
                                  +{tournament.standings.length - 6} more players
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No completed tournaments found</p>
                    <p>Tournament history will appear here once tournaments are completed</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading tournaments...</p>
          </div>
        )}
      </div>

      <QuestAccountModal
        isOpen={showQuestModal}
        onClose={() => setShowQuestModal(false)}
        onCreateAccount={handleCreateAccount}
      />

      <InviteCodeModal
          isOpen={showInviteModal}
          onClose={() => {
              setShowInviteModal(false)
              setSelectedTournament(null)
          }}
          onConfirm={(code) => performJoin(selectedTournament?.id, code)}
          loading={loading}
      />

      <PremiumTournamentModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        onUpgradeToPremium={handleUpgradeToPremium}
        isGuest={isGuest}
      />
    </div>
  )
}

export default Tournaments
